import { FloatingLabelInput } from '@/components/ui/input';
import { useForm } from '@tanstack/react-form';
import { Button } from '@/components/ui/button';
import { useGlobalStore } from '@/stores/store';
import { useMutation } from '@tanstack/react-query';
import { loginWithPostcode } from '@/services/apis/auth/loginWithPostcode';
import { POSTCODE_FORM } from '@/constants/auth.constants';
import { apiMutationKeys } from '@/services/apis/apiMutationKeys';
import { FieldError } from '@/components/ui/form-field-error';
import { replaceNonDigit } from '@/utils/string';
import { hasFieldErrors } from '@/utils/forms';
import { jwtDecodeCustomerId } from '@/utils/jwt';
import { z } from 'zod';
import { getGlobalStore } from '@/utils/store';

const PostcodeSchema = z.object({
  postcode: z.string().min(4, { message: 'Postcode must be 4 digits long' }),
});

const PostcodeForm = () => {
  const email = useGlobalStore((store) => store.authStore.authFlow.email);
  const { setAuthFlowCurrentStep, setInitialAccessToken, setCustomerId } =
    getGlobalStore();
  const handleSubmit = async ({ value }: { value: { postcode: string } }) => {
    try {
      await mutateAsync(
        { postcode: value.postcode, email },
        {
          onSuccess: (data) => {
            setInitialAccessToken(data.access_token);
            const customerId = jwtDecodeCustomerId(data.access_token);
            setCustomerId(customerId);
          },
        }
      );
      setAuthFlowCurrentStep('verifyAccount');
    } catch {
      // Silent error for API calls, user will be redirected to customerId screen
      // If there is an error, user should login using customerId (Fallback)
      setAuthFlowCurrentStep('customerId');
    }
  };
  const { mutateAsync, isPending } = useMutation({
    mutationFn: loginWithPostcode,
    mutationKey: [apiMutationKeys.basicLogin],
  });
  const form = useForm({
    defaultValues: {
      postcode: '',
    },
    validators: {
      onChange: PostcodeSchema,
    },
    onSubmit: handleSubmit,
  });

  return (
    <div className="flex flex-col gap-6 text-grey-900">
      <p className="text-xl font-nunito700">{POSTCODE_FORM.TITLE}</p>
      <p className="text-base">{POSTCODE_FORM.SUBTITLE}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field name="postcode">
          {(field) => (
            <div className="mb-6">
              <FloatingLabelInput
                maxLength={4}
                name="postcode"
                value={field.state.value}
                onChange={(e) =>
                  field.handleChange(replaceNonDigit(e.target.value))
                }
                label="Postcode"
                size="large"
                hasError={hasFieldErrors(field)}
              />
              <FieldError className="mt-1" field={field} />
            </div>
          )}
        </form.Field>
        <form.Subscribe
          selector={(state) => [state.canSubmit]}
          children={([canSubmit]) => (
            <Button
              variant="primary"
              type="submit"
              size="medium"
              disabled={!canSubmit}
              loading={isPending}
              className="flex-none"
            >
              {POSTCODE_FORM.SUBMIT_BUTTON}
            </Button>
          )}
        />
      </form>
    </div>
  );
};
export { PostcodeForm };
